import { useRef, useImperativeHandle, type Ref } from 'react';
import { useEvent } from 'expo';
import { useVideoPlayer, VideoView, VideoSource } from 'expo-video';
import { StyleSheet, View, Button, useWindowDimensions } from 'react-native';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppConsts from '@/utilities/AppConsts';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import { ExternalPathString, Link } from 'expo-router';

export type IVideo = {
  pause: () => void;
};

interface VideoProps {
  source: VideoSource,
  width?: number,
  height?: number,
  autoplay?: boolean,
  ref?: Ref<IVideo>;
  showLink?: boolean,
}

const VIDEO_BORDER_WIDTH = 20;
const VIDEO_WRAPPER_BORDER_WIDTH = 1;

const styles = StyleSheet.create({
  video: {
    borderRadius: VIDEO_BORDER_WIDTH,
  },
  videoWrapper: {
    borderRadius: VIDEO_BORDER_WIDTH,
    borderWidth: VIDEO_WRAPPER_BORDER_WIDTH,
    borderColor: AppConsts.COLOR6,
  },
  centerSelf: {
    alignSelf: 'center',
  },
});

export function getVideoSource(source: VideoSource) : VideoSource {
  if (typeof source == "string") {
    const uri = AppUtilities.getActualHostLink(false, true) + source;
    return {
      uri: uri,
      useCaching: true,
    };
  } else {
    return source;
  }
}

export function Video({
  source,
  autoplay = true,
  width = AppConsts.DEFAULT_VIDEO_WIDTH,
  height = AppConsts.DEFAULT_VIDEO_HEIGHT,
  ref,
  showLink = true,
}: VideoProps) {

  const {height : screenHeight, width : screenWidth} = useWindowDimensions();

  let finalWidth = width;
  let finalHeight = height;
  if (finalWidth == AppConsts.DEFAULT_VIDEO_WIDTH) {
    if (screenWidth > (AppConsts.DEFAULT_VIDEO_WIDTH * 2)) {
      finalWidth = Math.trunc(finalWidth * 1.5);
      finalHeight = Math.trunc(finalHeight * 1.5);
    }
  }

  const player = useVideoPlayer(
    getVideoSource(source),
    (player) => {
      player.loop = true;
      if (autoplay) {
        player.play();
      }
    }
  );

  useImperativeHandle(ref, () => ({
    pause: () => {
      if (player) {
        player.pause();
      }
    },
  }), [player]);
  
  let referenceLink : string | null = null;
  if (typeof source == "string" && showLink) {
    referenceLink = "https://www.youtube.com/watch?v=" + source.replaceAll("/videos/", "").replaceAll(".mp4", "");
  }

  if (AppUtilities.DEBUGLEVEL.layout) {
    AppUtilities.info("Video render width: " + width + ", height: " + height + ", screenWidth: " + screenWidth + ", screenHeight: " + screenHeight + ", finalWidth: " + finalWidth + ", finalHeight: " + finalHeight + " for " + referenceLink);
  }

  return (
    <View>
      <View
        style={[
          styles.videoWrapper,
          {
            width: finalWidth + (VIDEO_WRAPPER_BORDER_WIDTH * 2),
            height: finalHeight,
          },
        ]}
      >
        <VideoView
          style={[
            styles.video,
            {
              width: finalWidth,
              height: finalHeight,
            },
          ]}
          player={player}
          fullscreenOptions={{ enable: true }}
          allowsPictureInPicture={true}
        />
      </View>
      { referenceLink &&
          <Link
            href={referenceLink as ExternalPathString}
            target="_blank"
            style={[CustomTextStyles.linkTinyNoUnderline, styles.centerSelf]}
          >{referenceLink}</Link>
      }
    </View>
  );
}
